import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { 
  Bell, 
  BellRing, 
  X, 
  ShoppingCart, 
  User, 
  Clock,
  CheckCircle,
  Volume2,
  VolumeX
} from "lucide-react";
import axios from "axios";

const API = window.location.origin;

// مكون إشعار واحد
function NotificationItem({ notification, onDismiss }) {
  const isNew = !notification.read;
  const createdAt = new Date(notification.created_at);
  const timeAgo = getTimeAgo(createdAt);
  
  return (
    <div 
      className={`p-3 rounded-lg border transition-all ${
        isNew 
          ? 'bg-amber-500/10 border-amber-500/30 animate-pulse' 
          : 'bg-slate-800/50 border-slate-700'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isNew ? 'bg-amber-500/20' : 'bg-slate-700'
        }`}>
          <ShoppingCart className={`w-4 h-4 ${isNew ? 'text-amber-400' : 'text-slate-400'}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${isNew ? 'text-amber-300' : 'text-slate-300'}`}>
            {notification.title}
          </p>
          <p className="text-xs text-slate-400 mt-1 line-clamp-2">
            {notification.message}
          </p>
          
          {notification.details && (
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {notification.details.customer_name && (
                <span className="bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                  <User className="w-3 h-3 inline-block me-1" />
                  {notification.details.customer_name}
                </span>
              )}
              {notification.details.amount && (
                <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                  {notification.details.amount.toLocaleString()} د.ع
                </span>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-2 mt-2">
            <Clock className="w-3 h-3 text-slate-500" />
            <span className="text-xs text-slate-500">{timeAgo}</span>
          </div>
        </div>
        
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onDismiss(notification.id)}
          className="text-slate-500 hover:text-slate-300 p-1"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// حساب الوقت المنقضي
function getTimeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  
  if (seconds < 60) return 'الآن';
  if (seconds < 3600) return `منذ ${Math.floor(seconds / 60)} دقيقة`;
  if (seconds < 86400) return `منذ ${Math.floor(seconds / 3600)} ساعة`;
  return `منذ ${Math.floor(seconds / 86400)} يوم`;
}

// مكون الإشعارات الرئيسي
export default function PurchaseNotifications() {
  const { token, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastNotificationId, setLastNotificationId] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // تشغيل صوت الإشعار
  const playNotificationSound = useCallback(() => {
    if (soundEnabled) {
      try {
        // استخدام Web Audio API لإنشاء صوت بسيط
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.1;
        
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
        
        // صوت ثاني
        setTimeout(() => {
          const osc2 = audioContext.createOscillator();
          osc2.connect(gainNode);
          osc2.frequency.value = 1000;
          osc2.type = 'sine';
          osc2.start();
          osc2.stop(audioContext.currentTime + 0.15);
        }, 150);
      } catch (e) {
        console.log("Sound not supported");
      }
    }
  }, [soundEnabled]);
  
  // جلب الإشعارات
  const fetchNotifications = useCallback(async () => {
    if (!token || user?.role !== 'super_admin') return;
    
    try {
      const res = await axios.get(`${API}/api/referrals/admin/purchase-notifications`, { headers });
      const newNotifications = res.data.notifications || [];
      const newUnreadCount = res.data.unread_count || 0;
      
      // التحقق من وجود إشعارات جديدة
      if (newNotifications.length > 0 && newNotifications[0].id !== lastNotificationId) {
        if (lastNotificationId && newUnreadCount > unreadCount) {
          // إشعار جديد - تشغيل الصوت
          playNotificationSound();
          
          // إظهار إشعار المتصفح
          if (Notification.permission === "granted") {
            new Notification("طلب شراء جديد! 🛒", {
              body: newNotifications[0].message,
              icon: "/logo192.png"
            });
          }
        }
        setLastNotificationId(newNotifications[0].id);
      }
      
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [token, user, headers, lastNotificationId, unreadCount, playNotificationSound]);
  
  // طلب إذن الإشعارات
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);
  
  // Polling كل 30 ثانية
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);
  
  // تحديد الكل كمقروء
  const markAllRead = async () => {
    try {
      await axios.post(`${API}/api/referrals/admin/mark-all-notifications-read`, {}, { headers });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error("Error marking notifications read:", error);
    }
  };
  
  // إخفاء إشعار
  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  
  // لا تظهر للمستخدمين العاديين
  if (user?.role !== 'super_admin') return null;
  
  return (
    <div className="relative">
      {/* زر الإشعارات */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative text-slate-300 hover:text-white"
        data-testid="notifications-button"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 text-amber-400 animate-bounce" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
        
        {unreadCount > 0 && (
          <span className="absolute -top-1 -end-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>
      
      {/* قائمة الإشعارات */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <Card className="absolute top-full end-0 mt-2 w-80 sm:w-96 bg-slate-900 border-slate-700 shadow-xl z-50 max-h-[70vh] overflow-hidden" data-testid="notifications-panel">
            <div className="p-3 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-500" />
                طلبات الشراء
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount} جديد
                  </span>
                )}
              </h3>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="text-slate-400 hover:text-white p-1"
                  title={soundEnabled ? "إيقاف الصوت" : "تفعيل الصوت"}
                >
                  {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </Button>
                
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllRead}
                    className="text-slate-400 hover:text-white p-1"
                    title="تحديد الكل كمقروء"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            
            <CardContent className="p-2 max-h-[50vh] overflow-y-auto space-y-2">
              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">لا توجد إشعارات</p>
                </div>
              ) : (
                notifications.map(notification => (
                  <NotificationItem 
                    key={notification.id} 
                    notification={notification}
                    onDismiss={dismissNotification}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
